import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../hooks/redux';
import { createStripePaymentIntent } from '../../../services';
import { getApiErrorMessage } from '../../../utils/apiError';
import type { PaymentIntentBootstrapResult, PaymentIntentData } from '../../interfaces';
import {
  getOrCreatePaymentIntentRequest,
  getPaymentIntentBootstrapError,
  getPaymentIntentShippingAddress,
} from './paymentIntentBootstrap.helpers';
import { isStripeConfigured } from './stripe';
import { usePaymentIntentBootstrapData } from './usePaymentIntentBootstrapData';

export const usePaymentIntentBootstrap = (): PaymentIntentBootstrapResult => {
  const dispatch = useAppDispatch();
  const {
    cartItems,
    userId,
    shipping,
    guestEmail,
    cartValidationIssues,
    paymentRequestKey,
  } = usePaymentIntentBootstrapData();
  const [paymentData, setPaymentData] = useState<PaymentIntentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const bootstrapPayment = async () => {
      const bootstrapError = getPaymentIntentBootstrapError({
        cartItems,
        cartValidationIssues,
        hasStripePromise: isStripeConfigured,
      });

      if (bootstrapError) {
        setLoading(false);
        setErrorMessage(bootstrapError);
        return;
      }

      try {
        const payload = await getOrCreatePaymentIntentRequest(
          paymentRequestKey,
          () => {
            const shippingAddress = getPaymentIntentShippingAddress(shipping);

            return dispatch(
              createStripePaymentIntent({
                shippingAddress,
                guestEmail: userId ? undefined : guestEmail,
                guestCartItems: userId ? undefined : cartItems,
              })
            ).unwrap();
          }
        );

        if (active) {
          setPaymentData(payload);
          setErrorMessage(null);
        }
      } catch (error) {
        if (active) {
          setErrorMessage(getApiErrorMessage(error, 'Failed to initialize payment.'));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    bootstrapPayment();

    return () => {
      active = false;
    };
  }, [
    dispatch,
    cartItems,
    cartItems.length,
    cartValidationIssues,
    paymentRequestKey,
    shipping,
    guestEmail,
    userId,
  ]);

  return { paymentData, loading, errorMessage };
};
